import { useCallback, useState } from "react";
import cn from "classnames";

import { HabitIcon } from "@/components/ui/habit-icon";
import { ColorPicker } from "@/components/form/color-picker";
import { IconPicker } from "@/components/form/icon-picker";

import styles from "./category-picker.module.scss";

import type { Category } from "@hicya/habits-shared/db";

interface CategoryPickerProps {
  categories: Category[];
  value: string | null;
  onChange: (id: string | null) => void;
  onCreateCategory: (cat: { name: string; color: string; icon: string }) => void;
}

export function CategoryPicker({
  categories,
  value,
  onChange,
  onCreateCategory,
}: CategoryPickerProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#86b5c4");
  const [newIcon, setNewIcon] = useState("\u2B50");

  const handleCreate = useCallback(() => {
    if (!newName.trim()) return;
    onCreateCategory({
      name: newName.trim(),
      color: newColor,
      icon: newIcon,
    });
    setNewName("");
    setNewColor("#86b5c4");
    setNewIcon("\u2B50");
    setShowCreate(false);
  }, [newName, newColor, newIcon, onCreateCategory]);

  return (
    <div className={styles.container}>
      <div className={styles.grid} role="radiogroup" aria-label="Choose category">
        <button
          type="button"
          className={cn(styles.chip, value === null && styles.chipSelected)}
          onClick={() => onChange(null)}
          role="radio"
          aria-checked={value === null}
        >
          None
        </button>

        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={cn(styles.chip, value === cat.id && styles.chipSelected)}
            onClick={() => onChange(cat.id)}
            role="radio"
            aria-checked={value === cat.id}
          >
            <span className={styles.chipIcon}>
              <HabitIcon value={cat.icon} />
            </span>
            <span className={styles.chipColorDot} style={{ backgroundColor: cat.color }} />
            {cat.name}
          </button>
        ))}

        <button
          type="button"
          className={cn(styles.chip, styles.addChip)}
          onClick={() => setShowCreate(true)}
          aria-label="Create new category"
        >
          + New
        </button>
      </div>

      {showCreate && (
        <div className={styles.inlineForm}>
          <div className={styles.inlineRow}>
            <input
              type="text"
              className={styles.inlineInput}
              placeholder="Category name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={50}
              autoFocus
              aria-label="Category name"
            />
          </div>
          <IconPicker value={newIcon} onChange={setNewIcon} />
          <ColorPicker value={newColor} onChange={setNewColor} />
          <div className={styles.inlineActions}>
            <button
              type="button"
              className={styles.inlineCancel}
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.inlineSave}
              onClick={handleCreate}
              disabled={!newName.trim()}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
